import { Config } from 'config/envConfig';
import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import { getUrl } from '../components/pdfDisplay/utils';
import { alert, AlertType } from '@quyva/shared-ui';
import { ModelTiers } from '@quyva/common';

const GlobalStateContext = createContext();

export const GlobalStateProvider = ({ children }) => {
    const [transactionMode, setTransactionMode] = useState(false);

    const [availableCustomModels, setAvailableCustomModels] = useState([]);
    const [isLoadingModels, setIsLoadingModels] = useState(false);

    const [selectedTier, setSelectedTier] = useState(ModelTiers.STANDARD);
    const [customModel, setCustomModel] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [isOrgAdmin, setIsOrgAdmin] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(
        !!JSON.parse(localStorage.getItem('current') || 'null')?.is_super_admin
    );

    const getCurrentUser = () => {
        try {
            const user = JSON.parse(localStorage.getItem('current'));
            return user && user.email ? user : null;
        } catch (err) {
            console.error('Error parsing user from localStorage:', err);
            return null;
        }
    };

    const fetchOrgDetails = async () => {
        try {
            const orgResponse = await axios.get(
                getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/details`
                )
            );

            if (orgResponse.data?.details?.userRole === 'admin') {
                setIsOrgAdmin(true);
            } else {
                setIsOrgAdmin(false);
            }
        } catch (err) {
            console.warn('Failed to fetch org details:', err);
            setIsOrgAdmin(false);
        }
    };

    useEffect(() => {
        const fetchAvailableModels = async () => {
            setIsLoadingModels(true);
            try {
                // Get current user email for super admin check
                const currentUser = JSON.parse(localStorage.getItem('current') || '{}');
                const userEmail = currentUser?.email || '';

                const response = await axios.get(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/model_preferences_routes/api/available-models`
                    ),
                    {
                        params: { email: userEmail }
                    }
                );

                if (response.data) {
                    setAvailableCustomModels(response.data.custom_models);
                }
            } catch (error) {
                console.error('Error fetching available models:', error);
                alert(AlertType.ERROR, 'Failed to fetch available models');
            } finally {
                setIsLoadingModels(false);
            }
        };

        fetchAvailableModels();
        getAdminList();
        fetchOrgDetails();
    }, []);

    const fetchUserPreferences = async () => {
        const user = getCurrentUser();
        if (!user || !user.email) return;

        try {
            const response = await axios.get(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${
                        Config.FAST_API_PORT
                    }/model_preferences_routes/api/model-preferences/${encodeURIComponent(
                        user.email
                    )}`
                )
            );

            if (response.data) {
                setSelectedTier(response.data.model_tier);
                setCustomModel(response.data.custom_model?.name);

                // A downgrade of the account default is recorded in the bell
                // icon by the backend, so nothing is shown as a toast here. The
                // state set above already reflects the corrected selection.
            }
        } catch (error) {
            console.error('Error fetching user model preferences:', error);
            alert(AlertType.ERROR, 'Failed to fetch model preferences');
        }
    };

    useEffect(() => {
        fetchUserPreferences();
    }, []);
    const getAdminList = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('current'));

            // ✅ Skip silently if user is missing or token is undefined
            if (!user?.token) {
                console.warn('No logged-in user — skipping admin list fetch.');
                return;
            }

            const response = await axios.get(
                getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/AdminList`
                ),
                { responseType: 'text' }
            );

            const result = response.data;
            const isAdminCheck = result.includes(user.email);
            setIsAdmin(isAdminCheck);
        } catch (error) {
            // Show alert only if a user actually exists — avoid empty login popup
            const user = JSON.parse(localStorage.getItem('current'));
            if (user?.email) {
                alert(AlertType.ERROR, ` ${error.message}`);
            } else {
                console.warn(
                    'Skipping alert — user not logged in:',
                    error.message
                );
            }
        }
    };

    // inside GlobalStateProvider (add below other functions)

    // refresh user-specific data (public method)
    const refreshUserData = async (user) => {
        if (!user) {
            // reset if no user
            setIsAdmin(false);
            setIsOrgAdmin(undefined);
            setIsSuperAdmin(false);
            setSelectedTier(ModelTiers.STANDARD);
            setCustomModel('');
            return;
        }

        // run the same user-specific fetches the provider runs on user change
        try {
            // run in parallel
            await Promise.allSettled([
                getAdminList(user),
                fetchOrgDetails(user),
                fetchUserPreferences(user),
            ]);
            setIsSuperAdmin(!!user?.is_super_admin);
        } catch (err) {
            // we used Promise.allSettled so exceptions are already handled
            console.warn('refreshUserData error', err);
        }
    };

    return (
        <GlobalStateContext.Provider
            value={{
                transactionMode,
                setTransactionMode,
                availableCustomModels,
                isLoadingModels,
                selectedTier,
                customModel,
                setSelectedTier,
                setCustomModel,
                fetchUserPreferences,
                isAdmin: isAdmin || isSuperAdmin, //true if admin OR super admin
                isOrgAdmin: isOrgAdmin || isSuperAdmin, //true if org admin OR super admin
                isSuperAdmin,
                refreshUserData
            }}
        >
            {children}
        </GlobalStateContext.Provider>
    );
};

export const useGlobalCredits = () => {
    return useContext(GlobalStateContext);
};